import * as Dialog from '@radix-ui/react-dialog';
import { useState, type ReactNode } from 'react';
import { cn } from '../utils/cn.js';
import { Button } from './Button.js';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange?: (o: boolean) => void;
  /** Backward-compat alias for `onOpenChange(false)`. */
  onClose?: () => void;
  title: string;
  description?: ReactNode;
  /** Backward-compat alias for `description`. */
  body?: ReactNode;
  confirmLabel?: string;
  /** Backward-compat alias for `confirmLabel`. */
  confirmText?: string;
  /** Backward-compat alias for `confirmLabel`. */
  cta?: string;
  cancelLabel?: string;
  /** Backward-compat alias for `cancelLabel`. */
  cancelText?: string;
  danger?: boolean;
  /** Backward-compat: 'danger' | 'warning' | 'primary' → `danger` flag. */
  tone?: string;
  children?: ReactNode;
  /** If set, the confirm button is disabled until the user types this phrase verbatim. */
  typeToConfirm?: string;
  onConfirm: () => void | Promise<void>;
  className?: string;
}

/**
 * Kollez rule: only for irreversible actions (suspend tenant, decline offer, delete account,
 * void integrity flag, cancel drive with released offers). Undo toast handles the reversible cases.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  onClose,
  title,
  description,
  body,
  confirmLabel,
  confirmText,
  cta,
  cancelLabel,
  cancelText,
  danger,
  tone,
  children,
  typeToConfirm,
  onConfirm,
  className,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);

  const gated = typeToConfirm ? typed.trim() !== typeToConfirm : false;
  const isDanger = danger ?? (tone === 'danger' || tone === 'warning');
  const confirmCopy = confirmLabel ?? confirmText ?? cta ?? 'Confirm';
  const cancelCopy = cancelLabel ?? cancelText ?? 'Cancel';
  const bodyCopy: ReactNode = description ?? body ?? children ?? null;
  const handleOpenChange = (o: boolean) => {
    if (onOpenChange) onOpenChange(o);
    if (!o && onClose) onClose();
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="modal-overlay" />
        <Dialog.Content className={cn('modal', className)}>
          <Dialog.Title className="modal-title">{title}</Dialog.Title>
          <Dialog.Description className="modal-desc">{bodyCopy}</Dialog.Description>

          {typeToConfirm ? (
            <label className="modal-confirm">
              <span className="modal-confirm-hint">
                Type <b>{typeToConfirm}</b> to confirm
              </span>
              <input
                autoFocus
                className="input"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                aria-label={`Type ${typeToConfirm} to confirm`}
              />
            </label>
          ) : null}

          <div className="modal-foot">
            <Button variant="ghost" onClick={() => handleOpenChange(false)} disabled={busy}>
              {cancelCopy}
            </Button>
            <Button
              variant={isDanger ? 'danger' : 'primary'}
              disabled={gated || busy}
              loading={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await onConfirm();
                  handleOpenChange(false);
                } finally {
                  setBusy(false);
                  setTyped('');
                }
              }}
            >
              {confirmCopy}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
